import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  TrendingUp,
  TrendingDown,
  ShowChart,
  Assessment,
  Timeline,
  Speed,
  BarChart,
  Equalizer,
  ShoppingCart,
  Sell,
  SmartToy,
} from '@mui/icons-material';
import axios from 'axios';
import CandlestickChart from '../components/charts/CandlestickChart';
import TimeframeSelector from '../components/charts/TimeframeSelector';

const StockDetails = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [stockData, setStockData] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [news, setNews] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState('');
  const [aiError, setAiError] = useState('');
  const [orderType, setOrderType] = useState('BUY');
  const [shares, setShares] = useState('');
  const [balance, setBalance] = useState(0);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [tradeError, setTradeError] = useState('');
  const [tradeSuccess, setTradeSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [timeframe, setTimeframe] = useState('1M');
  const [chartLoading, setChartLoading] = useState(false);

  useEffect(() => {
    if (symbol) {
      fetchStockDetails();
      fetchBalance();
      fetchPriceData('1M');
    }
  }, [symbol]);

  const fetchPriceData = async (tf) => {
    try {
      setChartLoading(true);
      const response = await axios.get(`/stocks/prices/${symbol}?timeframe=${tf}`);
      setPriceData(response.data?.prices || null);
    } catch (err) {
      console.error('Error fetching price data:', err);
      setPriceData(null);
    } finally {
      setChartLoading(false);
    }
  };

  const handleTimeframeChange = (tf) => {
    setTimeframe(tf);
    fetchPriceData(tf);
  };

  const fetchBalance = async () => {
    try {
      var response = await axios.get('/trading/balance');
      setBalance(response.data.balance);
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const calculateTotal = () => {
    if (!stockData || !shares) return 0;
    return stockData.price * parseFloat(shares);
  };

  const handlePlaceOrder = async () => {
    try {
      setTradeLoading(true);
      setTradeError('');
      var response = await axios.post('/trading/order', {
        symbol: symbol.toUpperCase(),
        orderType: orderType,
        shares: parseFloat(shares),
        companyName: stockData.name || symbol
      });
      setTradeSuccess(orderType + ' order placed: ' + shares + ' shares of ' + symbol);
      setShares('');
      setConfirmDialog(false);
      fetchBalance();
    } catch (err) {
      setTradeError(err.response?.data?.message || 'Failed to place order');
    } finally {
      setTradeLoading(false);
    }
  };

  const fetchStockDetails = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch stock details, indicators, and news in parallel
      const [stockResponse, indicatorsResponse, newsResponse] = await Promise.all([
        axios.get(`/stocks/quote/${symbol}`),
        axios.get(`/stocks/indicators/${symbol}`),
        axios.get(`/stocks/news/${symbol}`)
      ]);

      setStockData(stockResponse.data);
      setIndicators(indicatorsResponse.data);
      setNews(newsResponse.data);

      // Automatically fetch AI analysis after basic data is loaded
      fetchAiAnalysis();

    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch stock details';
      setError(errorMessage);
      console.error('Stock details fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAiAnalysis = async (forceRefresh = false) => {
    try {
      setAiLoading(true);
      setAiError('');

      const url = forceRefresh 
        ? `/stocks/analysis/${symbol}?refresh=true`
        : `/stocks/analysis/${symbol}`;
      console.log(`Fetching AI analysis for ${symbol}, force: ${forceRefresh}`);
      const response = await axios.get(url, { timeout: 150000 }); // 2.5 min timeout for AI generation
      
      setAiAnalysis(response.data);
      console.log('AI analysis loaded successfully, source:', response.data.source);

    } catch (err) {
      const errorMessage = err.code === 'ECONNABORTED' 
        ? 'Analysis generation timed out. The AI analysis may still be processing — try refreshing in a minute.'
        : (err.response?.data?.message || 'Failed to fetch AI analysis');
      setAiError(errorMessage);
      console.error('AI analysis fetch error:', err);
    } finally {
      setAiLoading(false);
    }
  };

  const getAnalysisData = () => {
    if (!aiAnalysis?.analysis) return null;
    let data = aiAnalysis.analysis;
    // Handle multiple levels of string encoding
    while (typeof data === 'string') {
      try { data = JSON.parse(data); } catch (e) { return null; }
    }
    return data?.stockAnalysis || data;
  };

  // Extract targetPriceRange at component level so it's available in the top-level JSX
  const analysisDataTop = getAnalysisData();
  const targetPriceRange = analysisDataTop?.targetPriceRange || null;

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return 'N/A';
    return parseFloat(num).toFixed(decimals);
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined || isNaN(num)) return 'N/A';
    const value = parseFloat(num);
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatVolume = (volume) => {
    if (volume === null || volume === undefined || isNaN(volume)) return 'N/A';
    const vol = parseFloat(volume);
    if (vol >= 1000000) {
      return `${(vol / 1000000).toFixed(1)}M`;
    } else if (vol >= 1000) {
      return `${(vol / 1000).toFixed(1)}K`;
    }
    return vol.toLocaleString();
  };

  const getIndicatorColor = (value, type) => {
    if (value === null || value === undefined || isNaN(value)) return 'default';
    
    const val = parseFloat(value);
    
    switch (type) {
      case 'rsi':
        if (val > 70) return 'error'; // Overbought
        if (val < 30) return 'success'; // Oversold
        return 'warning';
      case 'macd':
        return val > 0 ? 'success' : 'error';
      case 'volume':
        return 'info';
      default:
        return val > 0 ? 'success' : 'error';
    }
  };

  const getIndicatorIcon = (type) => {
    switch (type) {
      case 'rsi':
        return <Speed />;
      case 'macd':
        return <Timeline />;
      case 'volume':
        return <BarChart />;
      case 'ema':
        return <ShowChart />;
      default:
        return <Equalizer />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading stock details for {symbol}...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={fetchStockDetails}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1">
            {symbol}
          </Typography>
          <Typography variant="h6" color="textSecondary">
            {stockData?.name || 'Stock Details'}
          </Typography>
        </Box>
        <Tooltip title="Ask AI Assistant about this stock">
          <IconButton
            color="primary"
            onClick={() => navigate(`/assistant`)}
            size="small"
          >
            <SmartToy />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stock Price Info */}
      {stockData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="body2" color="textSecondary">Current Price</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(stockData.price)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="body2" color="textSecondary">Change</Typography>
                <Typography 
                  variant="h6" 
                  color={parseFloat(stockData.change || 0) >= 0 ? 'success.main' : 'error.main'}
                  fontWeight="bold"
                >
                  {formatCurrency(stockData.change)} ({formatPercent(stockData.changePercent)})
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="body2" color="textSecondary">Day Range</Typography>
                <Typography variant="body1">
                  {formatCurrency(stockData.low)} – {formatCurrency(stockData.high)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="body2" color="textSecondary">52-Week Range</Typography>
                <Typography variant="body1">
                  {stockData.week52Low ? formatCurrency(stockData.week52Low) : 'N/A'} – {stockData.week52High ? formatCurrency(stockData.week52High) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="body2" color="textSecondary">Volume</Typography>
                <Typography variant="body1">
                  {formatVolume(stockData.volume)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="body2" color="textSecondary">Market Cap</Typography>
                <Typography variant="body1">
                  {stockData.marketCap ? `$${(stockData.marketCap / 1000000000).toFixed(1)}B` : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Target Price Range — above chart */}
      {targetPriceRange && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5 }}>Target Price Range</Typography>
            <Grid container spacing={2}>
              {targetPriceRange.currentPrice && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Current</Typography>
                  <Typography variant="h6">${targetPriceRange.currentPrice}</Typography>
                  {targetPriceRange.percentChange && (
                    <Typography variant="caption" color={parseFloat(targetPriceRange.percentChange) >= 0 ? 'success.main' : 'error.main'}>
                      {targetPriceRange.percentChange}
                    </Typography>
                  )}
                </Grid>
              )}
              {targetPriceRange.low && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">Low Target</Typography>
                  <Typography variant="h6" color="error.main">${targetPriceRange.low}</Typography>
                </Grid>
              )}
              {targetPriceRange.high && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">High Target</Typography>
                  <Typography variant="h6" color="success.main">${targetPriceRange.high}</Typography>
                </Grid>
              )}
              {targetPriceRange.targetPrice3Months && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">3-Month Target</Typography>
                  <Typography variant="h6" color="primary.main">${targetPriceRange.targetPrice3Months}</Typography>
                </Grid>
              )}
              {targetPriceRange.last5DaysPercentChange && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="textSecondary">5-Day Change</Typography>
                  <Typography variant="h6" color={parseFloat(targetPriceRange.last5DaysPercentChange) >= 0 ? 'success.main' : 'error.main'}>
                    {targetPriceRange.last5DaysPercentChange}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Candlestick Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Price Chart
            </Typography>
            <TimeframeSelector active={timeframe} onChange={handleTimeframeChange} />
          </Box>
          {chartLoading ? (
            <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading chart...</Typography>
            </Box>
          ) : (
            <CandlestickChart data={priceData} timeframe={timeframe} />
          )}
        </CardContent>
      </Card>

      {/* Trade Section */}
      {stockData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Trade {symbol}</Typography>

            {tradeError && <Alert severity="error" sx={{ mb: 2 }}>{tradeError}</Alert>}
            {tradeSuccess && <Alert severity="success" sx={{ mb: 2 }}>{tradeSuccess}</Alert>}

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <Typography variant="body2" color="textSecondary">Balance</Typography>
                <Typography variant="h6">{formatCurrency(balance)}</Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select value={orderType} label="Type" onChange={function(e) { setOrderType(e.target.value); }}>
                    <MenuItem value="BUY">Buy</MenuItem>
                    <MenuItem value="SELL">Sell</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label="Shares"
                  type="number"
                  value={shares}
                  onChange={function(e) { setShares(e.target.value); }}
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="body2" color="textSecondary">Total</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(calculateTotal())}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={function() { setConfirmDialog(true); }}
                  disabled={!shares || parseFloat(shares) <= 0 || tradeLoading}
                  color={orderType === 'BUY' ? 'primary' : 'secondary'}
                  startIcon={orderType === 'BUY' ? <ShoppingCart /> : <Sell />}
                >
                  {orderType} {symbol}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Technical Indicators */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {indicators ? (() => {
            const price = stockData?.price || stockData?.close;
            const indicatorRows = [];

            // Trend indicators
            if (indicators.ema_8) {
              indicatorRows.push({ category: 'Trend', name: 'EMA 8', value: formatCurrency(indicators.ema_8),
                signal: price > indicators.ema_8 ? 'Buy' : 'Sell',
                signalColor: price > indicators.ema_8 ? 'success' : 'error',
                note: `Price ${((price - indicators.ema_8) / indicators.ema_8 * 100).toFixed(2)}% ${price > indicators.ema_8 ? 'above' : 'below'}` });
            }
            if (indicators.ema_21) {
              indicatorRows.push({ category: 'Trend', name: 'EMA 21', value: formatCurrency(indicators.ema_21),
                signal: price > indicators.ema_21 ? 'Buy' : 'Sell',
                signalColor: price > indicators.ema_21 ? 'success' : 'error',
                note: `Price ${((price - indicators.ema_21) / indicators.ema_21 * 100).toFixed(2)}% ${price > indicators.ema_21 ? 'above' : 'below'}` });
            }
            if (indicators.ema_50) {
              indicatorRows.push({ category: 'Trend', name: 'EMA 50', value: formatCurrency(indicators.ema_50),
                signal: price > indicators.ema_50 ? 'Buy' : 'Sell',
                signalColor: price > indicators.ema_50 ? 'success' : 'error',
                note: `Price ${((price - indicators.ema_50) / indicators.ema_50 * 100).toFixed(2)}% ${price > indicators.ema_50 ? 'above' : 'below'}` });
            }

            // Momentum indicators
            if (indicators.rsi) {
              indicatorRows.push({ category: 'Momentum', name: 'RSI (14)', value: formatNumber(indicators.rsi, 2),
                signal: indicators.rsi > 70 ? 'Sell' : indicators.rsi < 30 ? 'Buy' : 'Neutral',
                signalColor: indicators.rsi > 70 ? 'error' : indicators.rsi < 30 ? 'success' : 'warning',
                note: indicators.rsi > 70 ? 'Overbought' : indicators.rsi < 30 ? 'Oversold' : 'Normal range' });
            }
            if (indicators.macd) {
              indicatorRows.push({ category: 'Momentum', name: 'MACD', value: formatNumber(indicators.macd, 4),
                signal: indicators.macd > (indicators.macd_signal || 0) ? 'Buy' : 'Sell',
                signalColor: indicators.macd > (indicators.macd_signal || 0) ? 'success' : 'error',
                note: `Signal: ${formatNumber(indicators.macd_signal, 4)} | Hist: ${formatNumber(indicators.macd_histogram, 4)}` });
            }
            if (indicators.stochastic_rsi) {
              indicatorRows.push({ category: 'Momentum', name: 'Stoch RSI', value: formatNumber(indicators.stochastic_rsi * 100, 1) + '%',
                signal: indicators.stochastic_rsi > 0.8 ? 'Sell' : indicators.stochastic_rsi < 0.2 ? 'Buy' : 'Neutral',
                signalColor: indicators.stochastic_rsi > 0.8 ? 'error' : indicators.stochastic_rsi < 0.2 ? 'success' : 'info',
                note: indicators.stochastic_rsi > 0.8 ? 'Overbought zone' : indicators.stochastic_rsi < 0.2 ? 'Oversold zone' : 'Normal range' });
            }
            if (indicators.momentum) {
              indicatorRows.push({ category: 'Momentum', name: 'Momentum (10)', value: formatNumber(indicators.momentum, 4),
                signal: indicators.momentum > 0 ? 'Buy' : 'Sell',
                signalColor: indicators.momentum > 0 ? 'success' : 'error',
                note: `${indicators.momentum > 0 ? 'Upward' : 'Downward'} momentum` });
            }

            // Volatility indicators
            if (indicators.bollinger_percent != null) {
              indicatorRows.push({ category: 'Volatility', name: 'Bollinger %B', value: formatNumber(indicators.bollinger_percent * 100, 1) + '%',
                signal: indicators.bollinger_percent > 0.8 ? 'Near Upper' : indicators.bollinger_percent < 0.2 ? 'Near Lower' : 'Mid Band',
                signalColor: indicators.bollinger_percent > 0.8 ? 'error' : indicators.bollinger_percent < 0.2 ? 'success' : 'info',
                note: `Band: ${formatCurrency(indicators.bollinger_lower)} — ${formatCurrency(indicators.bollinger_upper)}` });
            }
            if (indicators.bollinger_bandwidth != null) {
              indicatorRows.push({ category: 'Volatility', name: 'BB Width', value: formatNumber(indicators.bollinger_bandwidth * 100, 2) + '%',
                signal: indicators.bollinger_bandwidth > 0.15 ? 'Wide' : 'Normal',
                signalColor: 'info',
                note: 'Band squeeze detection' });
            }

            // Volume indicators
            if (indicators.price_volume) {
              indicatorRows.push({ category: 'Volume', name: 'Price × Volume', value: formatNumber(indicators.price_volume, 2),
                signal: 'Info', signalColor: 'info', note: 'Price-volume relationship' });
            }
            if (indicators.price_volume_trend) {
              indicatorRows.push({ category: 'Volume', name: 'PVT', value: formatNumber(indicators.price_volume_trend, 2),
                signal: indicators.price_volume_trend > 0 ? 'Buy' : 'Sell',
                signalColor: indicators.price_volume_trend > 0 ? 'success' : 'error',
                note: 'Price-volume trend' });
            }

            const buyCount = indicatorRows.filter(r => r.signal === 'Buy').length;
            const sellCount = indicatorRows.filter(r => r.signal === 'Sell').length;
            const neutralCount = indicatorRows.length - buyCount - sellCount;


            let prevCategory = '';

            return (
              <Box>
                {/* Summary */}
                <Grid container alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Assessment color="primary" />
                      <Typography variant="h6">Technical Indicators</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4} md={2} textAlign="center">
                    <Typography variant="h5" color="success.main" fontWeight="bold">{buyCount}</Typography>
                    <Typography variant="caption" color="textSecondary">Buy</Typography>
                  </Grid>
                  <Grid item xs={4} md={2} textAlign="center">
                    <Typography variant="h5" color="warning.main" fontWeight="bold">{neutralCount}</Typography>
                    <Typography variant="caption" color="textSecondary">Neutral</Typography>
                  </Grid>
                  <Grid item xs={4} md={2} textAlign="center">
                    <Typography variant="h5" color="error.main" fontWeight="bold">{sellCount}</Typography>
                    <Typography variant="caption" color="textSecondary">Sell</Typography>
                  </Grid>

                </Grid>

                {/* Price Data Row */}
                {(stockData?.open || stockData?.high || stockData?.low || stockData?.close || stockData?.volume) && (
                  <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {stockData?.open && <Chip variant="outlined" label={`Open: ${formatCurrency(stockData.open)}`} size="small" />}
                    {stockData?.high && <Chip variant="outlined" label={`High: ${formatCurrency(stockData.high)}`} size="small" />}
                    {stockData?.low && <Chip variant="outlined" label={`Low: ${formatCurrency(stockData.low)}`} size="small" />}
                    {stockData?.close && <Chip variant="outlined" label={`Close: ${formatCurrency(stockData.close)}`} size="small" />}
                    {stockData?.volume && <Chip variant="outlined" label={`Vol: ${formatVolume(stockData.volume)}`} size="small" />}
                  </Box>
                )}

                {/* Unified Table */}
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell><strong>Category</strong></TableCell>
                        <TableCell><strong>Indicator</strong></TableCell>
                        <TableCell align="center"><strong>Value</strong></TableCell>
                        <TableCell align="center"><strong>Signal</strong></TableCell>
                        <TableCell><strong>Notes</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {indicatorRows.map(function(row, i) {
                        const showCategory = row.category !== prevCategory;
                        prevCategory = row.category;
                        return (
                          <TableRow key={i} sx={{
                            bgcolor: row.signalColor === 'success' ? 'rgba(76,175,80,0.04)' :
                                     row.signalColor === 'error' ? 'rgba(244,67,54,0.04)' : 'transparent',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}>
                            <TableCell>
                              {showCategory ? <Typography variant="body2" fontWeight="bold">{row.category}</Typography> : ''}
                            </TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell align="center"><strong>{row.value}</strong></TableCell>
                            <TableCell align="center">
                              <Chip label={row.signal} color={row.signalColor} size="small" variant="filled" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="textSecondary">{row.note}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })() : (
            <Box>
              <Box display="flex" alignItems="center" mb={2}>
                <Assessment sx={{ mr: 1 }} />
                <Typography variant="h6">Technical Indicators</Typography>
              </Box>
              <Alert severity="info">
                No technical indicators available for {symbol}
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* AI Analysis Section */}
      <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Assessment sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Stock Analysis</Typography>
                  {aiAnalysis?.source === 'database' && (
                    <Chip label="Cached" size="small" variant="outlined" sx={{ ml: 1 }} />
                  )}
                  {aiAnalysis?.source === 'openai' && (
                    <Chip label="Fresh" size="small" color="success" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Box>
                {aiAnalysis && !aiLoading && aiAnalysis.ageMinutes >= 30 && (
                  <Button
                    variant="contained"
                    size="small"
                    color={aiAnalysis.isStale ? 'warning' : 'primary'}
                    onClick={() => fetchAiAnalysis(true)}
                    disabled={aiLoading}
                    startIcon={aiLoading ? <CircularProgress size={16} /> : <Assessment />}
                  >
                    Refresh Analysis
                  </Button>
                )}
              </Box>

              {aiError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {aiError}
                </Alert>
              )}

              {/* Analysis timestamp and price divergence warning */}
              {aiAnalysis && !aiLoading && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Analysis based on data as of {new Date(aiAnalysis.analysisDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                    {aiAnalysis.analysisPrice && ` (closing price: $${aiAnalysis.analysisPrice.toFixed(2)})`}
                  </Typography>
                  {aiAnalysis.analysisPrice && stockData?.price && (() => {
                    const priceDiff = ((stockData.price - aiAnalysis.analysisPrice) / aiAnalysis.analysisPrice) * 100;
                    if (Math.abs(priceDiff) >= 2) {
                      return (
                        <Alert severity="info" sx={{ mt: 1 }} variant="outlined">
                          Price has moved {priceDiff > 0 ? '+' : ''}{priceDiff.toFixed(1)}% since this analysis was generated. Indicators above reflect current data — consider refreshing the analysis.
                        </Alert>
                      );
                    }
                    return null;
                  })()}
                </Box>
              )}

              {aiLoading && (
                <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                  <CircularProgress sx={{ mr: 2 }} />
                  <Typography variant="body1">
                    Generating comprehensive stock analysis...
                  </Typography>
                </Box>
              )}

              {aiAnalysis && !aiLoading && (() => {
                const analysisData = getAnalysisData();
                
                if (!analysisData) {
                  // Fallback: render raw analysis as text
                  return (
                    <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {typeof aiAnalysis.analysis === 'string' ? aiAnalysis.analysis : JSON.stringify(aiAnalysis.analysis, null, 2)}
                      </Typography>
                    </Paper>
                  );
                }

                const { recommendation, targetPriceRange, technicalAnalysis, fundamentalAnalysis, companyInformation, newsAnalysis } = analysisData;

                return (
                  <Box>
                    {/* Analysis */}
                    <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Analysis</Typography>

                      {/* Backtesting Signal Row */}
                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
                        <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 'bold' }}>Backtesting:</Typography>
                        {aiAnalysis.tradingSignal ? (
                          <>
                            <Chip
                              label={aiAnalysis.tradingSignal.signal}
                              color={aiAnalysis.tradingSignal.signal?.toUpperCase() === 'BUY' ? 'success'
                                : aiAnalysis.tradingSignal.signal?.toUpperCase() === 'SELL' ? 'error' : 'warning'}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                            {aiAnalysis.tradingSignal.strategy_used && (
                              <Typography variant="body2" color="textSecondary">
                                {aiAnalysis.tradingSignal.strategy_used.replace('_', ' ')}
                                {aiAnalysis.tradingSignal.expected_return != null && (
                                  <span style={{ color: parseFloat(aiAnalysis.tradingSignal.expected_return) >= 0 ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                                    {' '}({parseFloat(aiAnalysis.tradingSignal.expected_return) >= 0 ? '+' : ''}{parseFloat(aiAnalysis.tradingSignal.expected_return).toFixed(2)}%)
                                  </span>
                                )}
                              </Typography>
                            )}
                            {aiAnalysis.tradingSignal.confidence && (
                              <Chip label={`${(parseFloat(aiAnalysis.tradingSignal.confidence) * 100).toFixed(0)}%`} variant="outlined" size="small" />
                            )}
                            <Typography variant="caption" color="textSecondary">
                              {aiAnalysis.tradingSignal.signal_date ? new Date(aiAnalysis.tradingSignal.signal_date).toLocaleDateString() : ''}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="textSecondary">No signal available</Typography>
                        )}
                      </Box>

                      {/* AI Recommendation Row */}
                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: recommendation?.reason ? 1.5 : 0 }}>
                        <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 'bold' }}>AI Analysis:</Typography>
                        {recommendation ? (
                          <>
                            <Chip
                              label={recommendation.action}
                              color={recommendation.action?.toUpperCase() === 'BUY' ? 'success'
                                : recommendation.action?.toUpperCase() === 'SELL' ? 'error' : 'warning'}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                            <Chip label={recommendation.confidence} variant="outlined" size="small" />
                          </>
                        ) : (
                          <Typography variant="body2" color="textSecondary">No AI recommendation available</Typography>
                        )}
                      </Box>
                      {recommendation?.reason && (
                        <Typography variant="body2" sx={{ lineHeight: 1.6, pl: '136px' }}>
                          {recommendation.reason}
                        </Typography>
                      )}

                      {/* Combined Signal Row */}
                      {aiAnalysis.tradingSignal?.final_signal && (
                        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                          <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 'bold' }}>Combined:</Typography>
                          <Chip
                            label={aiAnalysis.tradingSignal.final_signal}
                            color={aiAnalysis.tradingSignal.final_signal?.toUpperCase() === 'BUY' ? 'success'
                              : aiAnalysis.tradingSignal.final_signal?.toUpperCase() === 'SELL' ? 'error' : 'warning'}
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                          {aiAnalysis.tradingSignal.final_signal !== aiAnalysis.tradingSignal.signal && (
                            <Chip
                              label="🤖 AI Override"
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          )}
                          <Typography variant="caption" color="textSecondary">
                            Technical ({aiAnalysis.tradingSignal.signal}) + AI ({aiAnalysis.tradingSignal.ai_recommendation || 'N/A'})
                          </Typography>
                        </Box>
                      )}
                    </Paper>

                    {/* Technical Analysis */}
                    {(technicalAnalysis?.technical_recommendations || technicalAnalysis?.technicalRecommendation) && (() => {
                      const techRec = technicalAnalysis.technical_recommendations || technicalAnalysis.technicalRecommendation;
                      return (
                        <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="subtitle1" fontWeight="bold">AI Technical Analysis</Typography>
                            <Chip
                              label={techRec.action}
                              color={techRec.action?.toUpperCase() === 'BUY' ? 'success'
                                : techRec.action?.toUpperCase() === 'SELL' ? 'error' : 'warning'}
                              size="small"
                            />
                            {techRec.confidence && (
                              <Chip label={`Confidence: ${techRec.confidence}`} variant="outlined" size="small" />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                            {techRec.reason}
                          </Typography>
                        </Paper>
                      );
                    })()}

                    {/* Fundamental Analysis */}
                    {fundamentalAnalysis && (
                      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="subtitle1" fontWeight="bold">Fundamental Analysis</Typography>
                          <Chip
                            label={fundamentalAnalysis.action}
                            color={fundamentalAnalysis.action?.toUpperCase() === 'BUY' ? 'success'
                              : fundamentalAnalysis.action?.toUpperCase() === 'SELL' ? 'error' : 'warning'}
                            size="small"
                          />
                          <Chip label={`Confidence: ${fundamentalAnalysis.confidence}`} variant="outlined" size="small" />
                        </Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          {fundamentalAnalysis.reason}
                        </Typography>
                      </Paper>
                    )}

                    {/* News Analysis */}
                    {newsAnalysis && (
                      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="subtitle1" fontWeight="bold">AI News Analysis</Typography>
                          <Chip
                            label={newsAnalysis.sentiment}
                            color={newsAnalysis.sentiment?.toLowerCase() === 'positive' ? 'success'
                              : newsAnalysis.sentiment?.toLowerCase() === 'negative' ? 'error' : 'warning'}
                            size="small"
                          />
                        </Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          {newsAnalysis.summary}
                        </Typography>
                      </Paper>
                    )}

                    {/* Analysis Date */}
                    <Typography variant="caption" color="textSecondary">
                      Analysis date: {aiAnalysis.analysisDate ? new Date(aiAnalysis.analysisDate).toLocaleDateString() : 'N/A'}
                      {aiAnalysis.source === 'database' && ' (from cache)'}
                    </Typography>
                  </Box>
                );
              })()}

              {!aiAnalysis && !aiLoading && !aiError && (
                <Alert severity="info">
                  <Typography variant="body2">
                    AI analysis will be generated automatically when stock data is loaded.
                  </Typography>
                </Alert>
              )}
        </CardContent>
      </Card>

      {/* Trade Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={function() { setConfirmDialog(false); }}>
        <DialogTitle>Confirm {orderType} Order</DialogTitle>
        <DialogContent>
          <Typography>
            {orderType} {shares} shares of {symbol} for {formatCurrency(calculateTotal())}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={function() { setConfirmDialog(false); }}>Cancel</Button>
          <Button
            onClick={handlePlaceOrder}
            variant="contained"
            disabled={tradeLoading}
          >
            {tradeLoading ? 'Placing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!tradeSuccess}
        autoHideDuration={4000}
        onClose={function() { setTradeSuccess(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={function() { setTradeSuccess(''); }}>
          {tradeSuccess}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StockDetails;

